import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI, handleApiError } from '../../../services/api';
import './FeatureFlagManager.css';

const FeatureFlagManager = () => {
  const { t } = useTranslation();

  const [flags, setFlags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingKey, setTogglingKey] = useState(null);

  useEffect(() => {
    fetchFlags();
  }, []);

  const fetchFlags = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await adminAPI.getFeatureFlags();
      setFlags(response.data?.data || []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFlag = async (key, enabled) => {
    setTogglingKey(key);
    // Optimistic update; revert on failure.
    setFlags(prev => prev.map(f => (f.key === key ? { ...f, enabled } : f)));
    try {
      await adminAPI.updateFeatureFlag(key, enabled);
    } catch (err) {
      setFlags(prev => prev.map(f => (f.key === key ? { ...f, enabled: !enabled } : f)));
      setError(handleApiError(err));
    } finally {
      setTogglingKey(null);
    }
  };

  // snake_case key -> "Snake Case Key" for flags without a locale label yet.
  const humanize = (key) =>
    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  if (isLoading) {
    return (
      <div className="feature-flag-manager-loading">
        <div className="loading-spinner"></div>
        <p>{t('common.loading', 'Loading...')}</p>
      </div>
    );
  }

  return (
    <div className="feature-flag-manager">
      <div className="feature-flag-manager-header">
        <h2>{t('adminDashboard.menu.featureManagement', 'Feature Management')}</h2>
        <p className="feature-flag-manager-subtitle">
          {t('featureManagement.subtitle', 'Toggle platform behaviors. Changes take effect immediately.')}
        </p>
      </div>

      {error && <div className="feature-flag-manager-error">{error}</div>}

      {flags.length === 0 && !error && (
        <p className="feature-flag-manager-empty">
          {t('featureManagement.empty', 'No feature flags are defined yet.')}
        </p>
      )}

      <div className="feature-flag-manager-list">
        {flags.map((flag) => {
          const label = t(`featureManagement.flags.${flag.key}.label`, humanize(flag.key));
          const description = t(`featureManagement.flags.${flag.key}.description`, '');
          return (
            <div key={flag.key} className="feature-flag-card">
              <div className="feature-flag-card-text">
                <div className="feature-flag-card-label">{label}</div>
                {description && <div className="feature-flag-card-description">{description}</div>}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={flag.enabled}
                aria-label={label}
                disabled={togglingKey === flag.key}
                onClick={() => toggleFlag(flag.key, !flag.enabled)}
                className={`feature-flag-toggle ${flag.enabled ? 'on' : ''}`}
              >
                <span className="feature-flag-toggle-knob" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FeatureFlagManager;
